import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Button } from 'ui-components/components/button';
import BulkAction from '../../../../components/bulk-action';
import style from './bulk-update.module.less';
import Global from '../../../../store/global-selectors';
import { BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID } from '../../../../store/orders/drawer/constants';
import { BulkActions } from '../../../../enums/bulk-actions';
import { mapDispatchToOnClick, mapStateToOpen } from '../../../../containers/orders-drawer/sections/section-container-helper';
import * as BulkUpdateActions from '../../../../store/bulk/update/bulk.update.actions';
import { BulkUpdateService } from '../../../../services/bulk-update-service';
import messages from './messages';
import { BulkActionJobValidator, BulkActionJobErrorType } from '../../../../services/bulk-action-job-validator';
import { useRateShopper } from '../../../../pages/settings/rate-shopper/hooks/use-rate-shopper';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Text, TextType } from 'ui-components/components/text';

const sectionId = 'bulk-update';

const allowedBulkActions = [
  BulkActions.shipFrom,
  BulkActions.service,
  BulkActions.packageBulkAction,
  BulkActions.confirmation,
  BulkActions.weight,
  BulkActions.insurance,
];

export const BulkUpdate = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const bulkUpdateJobId = BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID;
  const confirmationTypes = useSelector((state) =>
    Global.selectBulkUpdateDrawerBulkUpdateProperty(state, {
      property: 'confirmationTypes',
    }),
  );
  const bulkJob = useSelector((state) => Global.selectBulkUpdateBulkUpdateJob(state, { bulkUpdateJobId })) || {};

  const { open } = useSelector((state) => mapStateToOpen(state, sectionId));

  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const { bulkActions = [], isLoading = false, model, jobData = {}, emptyBulkActions = [] } = bulkJob;
  const availableBulkActions = allowedBulkActions.filter((action) => !bulkActions.find((a) => a.name === action.name));
  jobData.bulkUpdateJobId = bulkUpdateJobId;

  const { onClick } = useMemo(() => mapDispatchToOnClick(dispatch, sectionId), [dispatch]);
  const { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate } = useRateShopper();
  const [bulkActionInlineMessage, setBulkActionInlineMessage] = useState(null);
  useEffect(() => {
    BulkActionJobValidator.validate(
      bulkActions,
      jobData,
      { ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate },
      BulkActionJobErrorType.InlineError,
    )
      .then(() => setBulkActionInlineMessage(null))
      .catch((errorMessage) => setBulkActionInlineMessage(errorMessage.message));
  }, [bulkActions, jobData, ignoreConfirmationBulkUpdate, ignorePackageBulkUpdate]);
  const addBulkAction = useCallback(
    (bulkActionToAdd) => {
      if (bulkActionToAdd.name === BulkActions.weight.name) {
        // other action types don't have to initialize because you first have to make a selection
        // but weight should be able to be set to 0 after initial inclusion
        dispatch(BulkUpdateActions.initializeZeroWeightBulkUpdate(sellerDefaultWeightUnit));
      }
      dispatch(
        BulkUpdateActions.updateBulkUpdateJob(bulkUpdateJobId, {
          bulkActions: [...bulkActions, bulkActionToAdd],
        }),
      );
    },
    [bulkActions, bulkUpdateJobId, dispatch, sellerDefaultWeightUnit],
  );

  const onModelChange = useCallback(
    (newModel) => dispatch(BulkUpdateActions.setModel(bulkUpdateJobId, newModel)),
    [bulkUpdateJobId, dispatch],
  );

  const removeBulkAction = useCallback(
    (bulkActionId) => {
      const remainingBulkActions = bulkActions.filter((action) => action.name !== bulkActionId);
      dispatch(
        BulkUpdateActions.updateBulkUpdateJob(bulkUpdateJobId, {
          bulkActions: remainingBulkActions,
        }),
      );
      dispatch(
        BulkUpdateActions.changeModel(
          bulkUpdateJobId,
          BulkUpdateService.cleanModelForBulkAction(model, bulkActionId, remainingBulkActions),
        ),
      );
    },
    [bulkActions, bulkUpdateJobId, dispatch, model],
  );

  const updateOrders = useCallback(() => dispatch(BulkUpdateActions.validateJobForDrawer(bulkUpdateJobId)), [bulkUpdateJobId, dispatch]);

  const clickCollapsibleHeaderSection = useCallback(() => onClick(open), [onClick, open]);

  return (
    <CollapsibleListSection open={open}>
      <CollapsibleListSectionHeader onClick={clickCollapsibleHeaderSection} theme='white'>
        <FormattedMessage {...messages.bulkUpdateHeaderDrawer} />
      </CollapsibleListSectionHeader>
      <CollapsibleListSectionContent>
        <div className={style['bulk-update-container']}>
          {isLoading && (
            <LoadingIndicator className={style['loading-indicator']} iconTitle={intl.formatMessage(messages.bulkUpdateLoadingTitle)} />
          )}
          {!isLoading && (
            <div className={style['bulk-update-non-loading-content']}>
              {bulkActionInlineMessage && (
                <BannerMessageV2
                  className={style['bulk-update-shipping-strategies-warning']}
                  content={<Text textType={TextType.Caption} value={bulkActionInlineMessage} />}
                  layout={BannerLayout.Inline}
                  status={BannerStatus.Warning}
                />
              )}
              {availableBulkActions.length > 0 && (
                <div className={style['available-bulk-actions']}>
                  {availableBulkActions.map((bulkAction) => (
                    <div key={bulkAction.name} className={style['available-bulk-action']}>
                      <Button onClick={() => addBulkAction(bulkAction)} theme='link'>
                        <FormattedMessage {...bulkAction.message} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className={style['bulk-actions']}>
                {bulkActions.map((bulkAction) => (
                  <div key={bulkAction.name} className={style['bulk-action-row']}>
                    <div className={style['bulk-action-editor']}>
                      <BulkAction
                        bulkActionId={bulkAction.name}
                        confirmationTypes={confirmationTypes}
                        isEmpty={!!emptyBulkActions.find((a) => a.name === bulkAction.name)}
                        jobData={jobData}
                        model={model}
                        onChange={onModelChange}
                      />
                    </div>
                    <Button
                      ariaLabel={intl.formatMessage(messages.bulkUpdateCloseButtonTitle)}
                      className={style['remove-bulk-action-button']}
                      onClick={() => removeBulkAction(bulkAction.name)}
                      theme='unstyled'
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </Button>
                  </div>
                ))}
              </div>
              <div className={style['section-footer']}>
                <Button disabled={bulkActions.length === 0} onClick={updateOrders} theme='primary'>
                  <FormattedMessage {...messages.bulkUpdateDrawerMainButton} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </CollapsibleListSectionContent>
    </CollapsibleListSection>
  );
};
